import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

type Onboarding = Awaited<ReturnType<typeof api.get<'/api/v1/onboarding'>>>;
type Step = Onboarding['currentStep'];

/**
 * The first ten minutes.
 *
 * The whole screen is built around one instruction from the plan: **do not force configuration
 * before first value.** Three things follow from it, and none of them is decoration.
 *
 * **Skip is a button, not a link in the corner.** A skip somebody has to hunt for is a skip they
 * take by abandoning the tab instead, and then there is no record of what they declined.
 *
 * **"Go to my mail" appears as soon as it is true.** The moment a mailbox is connected and backed
 * up, the remaining steps are useful and none of them is a gate — so the way out is offered rather
 * than hidden until the end.
 *
 * **Waiting says how long, or says it does not know.** A bar with no end is indistinguishable from
 * a hang, and the two waiting steps are where a flow like this loses people.
 */
export function OnboardingScreen(): ReactNode {
  const { t } = useI18n();
  const { activeWorkspace } = useAuth();

  const [state, setState] = useState<Onboarding | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [enteredAt] = useState(() => Date.now());

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        setState(await api.get('/api/v1/onboarding', undefined, { tenantId, signal }));
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const answer = useCallback(
    async (step: Step, skipped: boolean) => {
      if (tenantId === undefined) return;
      setBusy(true);
      try {
        /*
         * The duration travels with the step.
         *
         * It is the number that finds the step nobody understands: a step everybody completes and
         * everybody takes four minutes over is a step with a problem that completion counts cannot
         * see.
         */
        const next = await api.post(
          '/api/v1/onboarding/steps',
          { step, skipped, durationMs: Date.now() - enteredAt },
          undefined,
          { tenantId },
        );
        setState(next);
        setWaiting(false);
      } catch (error) {
        if (error instanceof ApiError && error.code === 'PRECONDITION_FAILED') {
          setWaiting(true);
          setFailure(null);
        } else {
          setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
        }
      } finally {
        setBusy(false);
      }
    },
    [tenantId, enteredAt],
  );

  const synchroniseRequiredStep = useCallback(async () => {
    if (tenantId === undefined || state === null) return;
    const step = state.nextStep;
    if (step !== 'connect_mailbox' && step !== 'inventory' && step !== 'vault_snapshot') return;

    try {
      const mailboxes = await api.get('/api/v1/mailboxes', undefined, { tenantId });
      const statuses = mailboxes.mailboxes.map((mailbox) => mailbox.status);
      const ready =
        step === 'connect_mailbox'
          ? statuses.length > 0
          : step === 'inventory'
            ? statuses.some((status) =>
                [
                  'backup_pending',
                  'backing_up',
                  'snapshot_verified',
                  'rescuing',
                  'active',
                  'paused',
                ].includes(status),
              )
            : statuses.some((status) =>
                ['snapshot_verified', 'rescuing', 'active', 'paused'].includes(status),
              );

      if (ready) {
        await answer(step, false);
      } else {
        setWaiting(true);
      }
    } catch (error) {
      setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
    }
  }, [answer, state, tenantId]);

  useEffect(() => {
    if (
      state?.nextStep !== 'connect_mailbox' &&
      state?.nextStep !== 'inventory' &&
      state?.nextStep !== 'vault_snapshot'
    ) {
      return;
    }

    void synchroniseRequiredStep();
    const timer = window.setInterval(() => void synchroniseRequiredStep(), 5_000);
    return () => window.clearInterval(timer);
  }, [state?.nextStep, synchroniseRequiredStep]);

  const leave = useCallback(
    async (completed = false) => {
      if (tenantId === undefined) return;
      /* Recorded as reaching Today rather than as dismissing, because they are different things and
       only one of them is the design working. */
      await api.post(
        '/api/v1/onboarding/finish',
        { code: completed ? 'completed' : 'reached_today' },
        undefined,
        { tenantId },
      );
      window.location.assign('/');
    },
    [tenantId],
  );

  const performAction = (step: Step) => {
    if (step === 'connect_mailbox') {
      window.location.assign('/settings/mailboxes?onboarding=1');
      return;
    }
    if (step === 'rescue_start') {
      window.location.assign('/rescue?onboarding=1');
      return;
    }
    if (step === 'inventory' || step === 'vault_snapshot') {
      void synchroniseRequiredStep();
      return;
    }
    void answer(step, false);
  };

  if (failure !== null && state === null) {
    return (
      <Alert tone="critical" title={t('onboarding.error.title')}>
        <p>{t('onboarding.error.body')}</p>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (state === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  const step = state.nextStep;

  if (step === null) {
    return (
      <section className="card">
        <h1 className="card__title">{t('onboarding.done.title')}</h1>
        <p className="empty__body">{t('onboarding.done.body')}</p>
        <Button onClick={() => void leave(true)}>{t('onboarding.goToMail')}</Button>
      </section>
    );
  }

  const position = state.completed.length + state.skipped.length + 1;

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t(`onboarding.step.${step}.title`)}</h1>
        <p className="empty__body">{t(`onboarding.step.${step}.body`)}</p>
        {/*
          A count, not a percentage bar.

          "Step 3 of 10" is honest about a flow somebody can leave; a bar implies that reaching the
          end is the point, and here it is not.
        */}
        <p className="today-list__meta">{t('onboarding.position', { position, total: 10 })}</p>
      </header>

      {state.progress !== null && (
        <section className="card" aria-live="polite">
          <p>{t(state.progress.key, state.progress.params)}</p>
          {/*
            Long waits offer a way out instead of asking somebody to watch. A person who leaves and
            comes back to a finished mailbox has a better first experience than one who watched a
            bar for twenty minutes.
          */}
          {state.progress.longWait && <p className="empty__body">{t('onboarding.longWait')}</p>}
        </section>
      )}

      {waiting && (
        <Alert tone="info" title={t('onboarding.waiting.title')}>
          {t('onboarding.waiting.body')}
        </Alert>
      )}

      <div className="onboarding-actions">
        <Button disabled={busy} onClick={() => performAction(step)}>
          {t(`onboarding.step.${step}.action`)}
        </Button>

        {/*
          Skip is a real button beside the primary one.

          A skip somebody has to hunt for is a skip they take by closing the tab instead, and then
          nothing is recorded about what they declined.
        */}
        {state.maySkipCurrent && (
          <Button disabled={busy} onClick={() => void answer(step, true)} variant="ghost">
            {t('onboarding.skip')}
          </Button>
        )}
      </div>

      {/*
        The way out, offered the moment it is true rather than held until the end. This is the
        plan's constraint rendered as a button.
      */}
      {state.mayEnterProduct && (
        <p className="onboarding-leave">
          <button className="link" onClick={() => void leave()} type="button">
            {t('onboarding.goToMail')}
          </button>
        </p>
      )}

      {failure !== null && (
        <Alert tone="critical" title={t('onboarding.error.title')}>
          {t('onboarding.error.body')}
        </Alert>
      )}
    </>
  );
}
